import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import ExportModal from '../components/ExportModal';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, AreaChart, Area,
  Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend 
} from 'recharts';
import { 
  TrendingUp, Send, CheckCircle2, Eye, XCircle, Filter, Calendar, 
  Download, RefreshCw, ChevronDown, BarChart2, Activity
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Wrapper pour le support embedded - crée un composant simple qui n'utilise pas Layout
const AnalyticsContent = ({ embedded }) => {
  const { token } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [advancedAnalytics, setAdvancedAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    campaign_id: '',
    status: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignAnalytics, setCampaignAnalytics] = useState(null);

  useEffect(() => {
    fetchAnalytics();
    fetchAdvancedAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API_URL}/analytics/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdvancedAnalytics = async () => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.campaign_id) params.append('campaign_id', filters.campaign_id);
      if (filters.status) params.append('status', filters.status);

      const response = await axios.get(`${API_URL}/analytics/advanced?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdvancedAnalytics(response.data);
      setCampaigns(response.data.available_campaigns || []);
    } catch (error) {
      console.error('Advanced analytics error:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const fetchCampaignAnalytics = async (campaignId) => {
    try {
      const response = await axios.get(`${API_URL}/campaigns/${campaignId}/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampaignAnalytics(response.data);
      setSelectedCampaign(campaigns.find(c => c.id === campaignId));
    } catch (error) {
      toast.error('Erreur lors du chargement des analytics de campagne');
    }
  };

  const applyFilters = () => {
    fetchAdvancedAnalytics();
  };

  const resetFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      campaign_id: '',
      status: ''
    });
    setTimeout(fetchAdvancedAnalytics, 100);
  };

  const exportData = () => {
    const data = advancedAnalytics?.timeline || [];
    const csv = [
      ['Date', 'Total', 'Envoyés', 'Livrés', 'Lus', 'Échoués'],
      ...data.map(row => [row.date, row.total, row.sent, row.delivered, row.read, row.failed])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Export CSV téléchargé');
  };

  const Wrapper = embedded ? React.Fragment : Layout;

  if (loading) {
    return (
      <Wrapper>
        <div className="flex items-center justify-center h-64" data-testid="loading">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-700"></div>
        </div>
      </Wrapper>
    );
  }

  const statusData = analytics?.messages_by_status ? [
    { name: 'Envoyés', value: analytics.messages_by_status.sent, color: '#6366f1' },
    { name: 'Livrés', value: analytics.messages_by_status.delivered, color: '#22c55e' },
    { name: 'Lus', value: analytics.messages_by_status.read, color: '#3b82f6' },
    { name: 'Échoués', value: analytics.messages_by_status.failed, color: '#ef4444' }
  ] : [];

  const deliveryRate = analytics?.total_messages > 0
    ? ((analytics.messages_by_status.delivered / analytics.total_messages) * 100).toFixed(1)
    : 0;

  const readRate = analytics?.total_messages > 0
    ? ((analytics.messages_by_status.read / analytics.total_messages) * 100).toFixed(1)
    : 0;

  const timelineData = advancedAnalytics?.timeline || [];

  return (
    <Wrapper>
      <div className="space-y-8" data-testid="analytics-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Analytics & Rapports
            </h1>
            <p className="mt-2 text-slate-600">Suivez les performances de vos campagnes RCS</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="rounded-full"
              data-testid="toggle-filters-button"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filtres
              <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </Button>
            <Button
              variant="outline"
              onClick={() => setExportModalOpen(true)}
              className="rounded-full"
              data-testid="export-button"
            >
              <Download className="h-4 w-4 mr-2" />
              Exporter
            </Button>
            <Button
              onClick={() => { fetchAnalytics(); fetchAdvancedAnalytics(); }}
              disabled={refreshing}
              className="rounded-full bg-indigo-700"
              data-testid="refresh-button"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <Card className="rounded-2xl border-slate-100" data-testid="filters-panel">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600">Date de début</label>
                  <Input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                    className="rounded-xl h-11"
                    data-testid="start-date-filter"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600">Date de fin</label>
                  <Input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                    className="rounded-xl h-11"
                    data-testid="end-date-filter"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600">Campagne</label>
                  <Select
                    value={filters.campaign_id || "all"}
                    onValueChange={(value) => setFilters({ ...filters, campaign_id: value === "all" ? "" : value })}
                  >
                    <SelectTrigger className="rounded-xl h-11" data-testid="campaign-filter">
                      <SelectValue placeholder="Toutes les campagnes" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les campagnes</SelectItem>
                      {campaigns.map((campaign) => (
                        <SelectItem key={campaign.id} value={campaign.id}>
                          {campaign.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600">Statut</label>
                  <Select
                    value={filters.status || "all"}
                    onValueChange={(value) => setFilters({ ...filters, status: value === "all" ? "" : value })}
                  >
                    <SelectTrigger className="rounded-xl h-11" data-testid="status-filter">
                      <SelectValue placeholder="Tous les statuts" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les statuts</SelectItem>
                      <SelectItem value="pending">En attente</SelectItem>
                      <SelectItem value="sent">Envoyé</SelectItem>
                      <SelectItem value="delivered">Livré</SelectItem>
                      <SelectItem value="read">Lu</SelectItem>
                      <SelectItem value="failed">Échoué</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  <Button onClick={applyFilters} className="rounded-full bg-indigo-700 flex-1" data-testid="apply-filters-button">
                    Appliquer
                  </Button>
                  <Button variant="outline" onClick={resetFilters} className="rounded-full" data-testid="reset-filters-button">
                    Reset
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="rounded-2xl border-slate-100 hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Total Messages</p>
                  <p className="mt-2 text-3xl font-bold text-slate-900" data-testid="total-messages">
                    {(advancedAnalytics?.summary?.total_messages || analytics?.total_messages || 0).toLocaleString()}
                  </p>
                </div>
                <div className="rounded-xl bg-indigo-50 p-3">
                  <Send className="h-8 w-8 text-indigo-700" />
                </div>
              </div>
              <div className="mt-3 flex items-center text-sm text-green-600">
                <TrendingUp className="mr-1 h-4 w-4" />
                <span>+12% ce mois</span>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-slate-100 hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Taux de livraison</p>
                  <p className="mt-2 text-3xl font-bold text-green-600" data-testid="delivery-rate">
                    {deliveryRate}%
                  </p>
                </div>
                <div className="rounded-xl bg-green-50 p-3">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                </div>
              </div>
              <div className="mt-3 text-sm text-slate-500">
                {analytics?.messages_by_status?.delivered?.toLocaleString() || 0} messages livrés
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-slate-100 hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Taux de lecture</p>
                  <p className="mt-2 text-3xl font-bold text-blue-600" data-testid="read-rate">
                    {readRate}%
                  </p>
                </div>
                <div className="rounded-xl bg-blue-50 p-3">
                  <Eye className="h-8 w-8 text-blue-600" />
                </div>
              </div>
              <div className="mt-3 text-sm text-slate-500">
                {analytics?.messages_by_status?.read?.toLocaleString() || 0} messages lus
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-slate-100 hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Taux d'échec</p>
                  <p className="mt-2 text-3xl font-bold text-red-600" data-testid="failure-rate">
                    {analytics?.total_messages > 0
                      ? ((analytics.messages_by_status.failed / analytics.total_messages) * 100).toFixed(1)
                      : 0}%
                  </p>
                </div>
                <div className="rounded-xl bg-red-50 p-3">
                  <XCircle className="h-8 w-8 text-red-600" />
                </div>
              </div>
              <div className="mt-3 text-sm text-slate-500">
                {analytics?.messages_by_status?.failed?.toLocaleString() || 0} messages échoués
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Timeline Chart */}
        {timelineData.length > 0 && (
          <Card className="rounded-2xl border-slate-100">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-indigo-50 p-2">
                  <Activity className="h-5 w-5 text-indigo-700" />
                </div>
                <div>
                  <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Évolution temporelle</CardTitle>
                  <CardDescription>Messages par jour avec détails des statuts</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={timelineData}>
                  <defs>
                    <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorDelivered" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#64748b" 
                    tickFormatter={(value) => new Date(value).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                  />
                  <YAxis stroke="#64748b" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '12px',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }}
                    labelFormatter={(value) => new Date(value).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                  />
                  <Legend />
                  <Area type="monotone" dataKey="total" name="Total" stroke="#6366f1" fillOpacity={1} fill="url(#colorTotal)" />
                  <Area type="monotone" dataKey="delivered" name="Livrés" stroke="#22c55e" fillOpacity={1} fill="url(#colorDelivered)" />
                  <Line type="monotone" dataKey="read" name="Lus" stroke="#3b82f6" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="failed" name="Échoués" stroke="#ef4444" strokeWidth={2} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="rounded-2xl border-slate-100">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Répartition des statuts</CardTitle>
              <CardDescription>Vue d'ensemble des statuts de messages</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value, percent }) => value > 0 ? `${name}: ${(percent * 100).toFixed(0)}%` : ''}
                    outerRadius={100}
                    innerRadius={40}
                    fill="#8884d8"
                    dataKey="value"
                    paddingAngle={2}
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => value.toLocaleString()}
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-slate-100">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Statistiques par statut</CardTitle>
              <CardDescription>Nombre de messages par statut</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={statusData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" stroke="#64748b" />
                  <YAxis type="category" dataKey="name" stroke="#64748b" width={80} />
                  <Tooltip
                    formatter={(value) => value.toLocaleString()}
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Campaign Selection for Detailed Analytics */}
        <Card className="rounded-2xl border-slate-100">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-purple-50 p-2">
                  <BarChart2 className="h-5 w-5 text-purple-700" />
                </div>
                <div>
                  <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Analytics par campagne</CardTitle>
                  <CardDescription>Sélectionnez une campagne pour voir les détails</CardDescription>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
              {campaigns.map((campaign) => (
                <button
                  key={campaign.id}
                  onClick={() => fetchCampaignAnalytics(campaign.id)}
                  className={`p-4 rounded-xl border-2 text-left transition-all hover:shadow-md ${
                    selectedCampaign?.id === campaign.id
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-slate-200 hover:border-indigo-200'
                  }`}
                  data-testid={`campaign-analytics-btn-${campaign.id}`}
                >
                  <p className="font-medium text-slate-900 truncate">{campaign.name}</p>
                  <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                    campaign.status === 'sent' ? 'bg-green-100 text-green-700' :
                    campaign.status === 'active' ? 'bg-blue-100 text-blue-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {campaign.status === 'sent' ? 'Envoyée' :
                     campaign.status === 'active' ? 'Active' : 'Brouillon'}
                  </span>
                </button>
              ))}
              {campaigns.length === 0 && (
                <p className="text-slate-500 col-span-full text-center py-8">
                  Aucune campagne disponible
                </p>
              )}
            </div>

            {/* Campaign Details */}
            {campaignAnalytics && (
              <div className="border-t border-slate-100 pt-6" data-testid="campaign-analytics-details">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">
                  {campaignAnalytics.campaign_name}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                  <div className="bg-slate-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-slate-900">{campaignAnalytics.summary?.total_recipients?.toLocaleString()}</p>
                    <p className="text-xs text-slate-500 mt-1">Total</p>
                  </div>
                  <div className="bg-indigo-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-indigo-700">{campaignAnalytics.summary?.sent?.toLocaleString()}</p>
                    <p className="text-xs text-slate-500 mt-1">Envoyés</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">{campaignAnalytics.summary?.delivered?.toLocaleString()}</p>
                    <p className="text-xs text-slate-500 mt-1">Livrés</p>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{campaignAnalytics.summary?.read?.toLocaleString()}</p>
                    <p className="text-xs text-slate-500 mt-1">Lus</p>
                  </div>
                  <div className="bg-red-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-red-600">{campaignAnalytics.summary?.failed?.toLocaleString()}</p>
                    <p className="text-xs text-slate-500 mt-1">Échoués</p>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-purple-600">{campaignAnalytics.summary?.read_rate}%</p>
                    <p className="text-xs text-slate-500 mt-1">Taux lecture</p>
                  </div>
                </div>

                {/* Campaign Timeline */}
                {campaignAnalytics.timeline && campaignAnalytics.timeline.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-slate-600 mb-3">Évolution de la campagne</h4>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={campaignAnalytics.timeline}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="hour" stroke="#64748b" fontSize={10} />
                        <YAxis stroke="#64748b" />
                        <Tooltip />
                        <Line type="monotone" dataKey="sent" name="Envoyés" stroke="#6366f1" strokeWidth={2} />
                        <Line type="monotone" dataKey="delivered" name="Livrés" stroke="#22c55e" strokeWidth={2} />
                        <Line type="monotone" dataKey="read" name="Lus" stroke="#3b82f6" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary Stats */}
        <Card className="rounded-2xl border-slate-100">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Résumé des performances</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl">
                <div className="text-4xl font-bold text-indigo-700" data-testid="total-campaigns-stat">
                  {analytics?.total_campaigns || 0}
                </div>
                <p className="mt-2 text-sm text-slate-600">Campagnes totales</p>
              </div>
              <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl">
                <div className="text-4xl font-bold text-purple-600" data-testid="total-contacts-stat">
                  {analytics?.total_contacts || 0}
                </div>
                <p className="mt-2 text-sm text-slate-600">Contacts totaux</p>
              </div>
              <div className="text-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl">
                <div className="text-4xl font-bold text-green-600" data-testid="success-rate-stat">
                  {analytics?.total_messages > 0
                    ? (((analytics.messages_by_status.delivered + analytics.messages_by_status.read) / analytics.total_messages) * 100).toFixed(1)
                    : 0}%
                </div>
                <p className="mt-2 text-sm text-slate-600">Taux de succès global</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Export Modal */}
      <ExportModal 
        open={exportModalOpen} 
        onOpenChange={setExportModalOpen}
        defaultType="campaigns"
      />
    </Wrapper>
  );
};

// Composant principal avec support embedded
const Analytics = ({ embedded = false }) => {
  return <AnalyticsContent embedded={embedded} />;
};

export default Analytics;
